import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { BackButton, FormField, FormGrid, FormSection, PrimaryButton } from 'components/forms';
import moment, { type Moment } from 'moment';
import type React from 'react';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import { useUpdateTicketMutation } from 'services/api/tickets.api';
import {
  SELECTABLE_TICKET_MOTIVES,
  TICKET_MOTIVE_LABELS,
  TICKET_URGENCIES,
  TICKET_URGENCY_LABELS,
  type Ticket,
  type TicketMotive,
  type TicketUrgency,
} from 'services/types/ticket';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  STATUS_WARN,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

const FONT = 'Jost, sans-serif';

const glassPaperSx = {
  backdropFilter: GLASS_BLUR,
  backgroundColor: GLASS_BACKGROUND,
  boxShadow: SHADOW,
  borderRadius: RADIUS_CARD,
  border: `1px solid ${alpha(NAVY, 0.06)}`,
} as const;

const glassBackdropSx = {
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(NAVY, 0.25),
} as const;

const multiSelectSx = {
  width: '100%',
  '&.MuiInputBase-root, &.MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    bgcolor: SURFACE_CONTAINER_LOWEST,
    boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
    transition: 'box-shadow 0.18s ease',
    fontFamily: FONT,
    fontSize: '0.9375rem',
    '& fieldset': { border: 'none' },
    '&:hover': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` },
    '&.Mui-focused': {
      boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`,
    },
    '&.Mui-disabled': {
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
      bgcolor: SURFACE_CONTAINER_LOWEST,
    },
  },
  '& .MuiSelect-select': { py: 1.25, px: 1.75, color: NAVY, minHeight: '24px' },
} as const;

const datePickerSx = {
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    bgcolor: `${SURFACE_CONTAINER_LOWEST} !important`,
    boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
    transition: 'box-shadow 0.18s ease',
    fontFamily: FONT,
    fontSize: '0.9375rem',
    '& fieldset, & .MuiPickersOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` },
    '&.Mui-focused': { boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}` },
    '&.Mui-disabled': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}` },
  },
  '& .MuiPickersInputBase-sectionsContainer, & .MuiOutlinedInput-input': {
    py: 1.25,
    px: 1.75,
    color: NAVY,
  },
} as const;

interface FieldShellProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string | null;
  hint?: ReactNode;
  children: ReactNode;
}

const FieldShell: FC<FieldShellProps> = ({ label, required, optional, error, hint, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
    <Typography
      component="label"
      sx={{
        fontFamily: FONT,
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: alpha(NAVY, 0.7),
        mb: 0.875,
        letterSpacing: '0.005em',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {label}
      {required && (
        <Typography component="span" sx={{ color: '#E7233B', fontWeight: 600, ml: 0.25 }} aria-hidden>
          *
        </Typography>
      )}
      {optional && !required && (
        <Typography component="span" sx={{ color: alpha(NAVY, 0.45), fontWeight: 400, fontSize: '0.75rem', ml: 0.5 }}>
          (opcional)
        </Typography>
      )}
    </Typography>
    {children}
    {(error || hint) && (
      <Typography
        sx={{
          mt: 0.625,
          fontFamily: FONT,
          fontSize: '0.75rem',
          color: error ? '#E7233B' : alpha(NAVY, 0.55),
          lineHeight: 1.45,
        }}
      >
        {error || hint}
      </Typography>
    )}
  </Box>
);

interface EditTicketDialogProps {
  open: boolean;
  onClose: () => void;
  ticket: Ticket;
  onSuccess?: () => void;
}

const urgencyDotColor = (r: TicketUrgency): string =>
  r === 'HIGH' ? STATUS_WARN : r === 'MEDIUM' ? '#A8810A' : `${NAVY}99`;

const EditTicketDialog: React.FC<EditTicketDialogProps> = ({ open, onClose, ticket, onSuccess }) => {
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [motive, setMotive] = useState<TicketMotive | ''>(ticket.motive ?? '');
  const [urgency, setUrgency] = useState<TicketUrgency | ''>(ticket.urgency ?? '');
  const [deadlineDate, setDeadlineDate] = useState<Moment | null>(
    ticket.deadlineDate ? moment(ticket.deadlineDate) : null
  );
  const [updateTicket, { isLoading }] = useUpdateTicketMutation();

  useEffect(() => {
    if (open) {
      setTitle(ticket.title);
      setDescription(ticket.description);
      setMotive(ticket.motive ?? '');
      setUrgency(ticket.urgency ?? '');
      setDeadlineDate(ticket.deadlineDate ? moment(ticket.deadlineDate) : null);
    }
  }, [open, ticket]);

  // Tickets de Rollbar mantienen su motive automático y no son editables vía este dialog
  const isRollbarTicket = ticket.motive === 'ROLLBAR';

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) {
      return;
    }

    updateTicket({
      ticketId: ticket.id,
      data: {
        title: title.trim(),
        description: description.trim(),
        ...(isRollbarTicket
          ? {}
          : {
              motive: motive === '' ? null : motive,
              urgency: urgency === '' ? null : urgency,
              deadlineDate: deadlineDate ? deadlineDate.toISOString() : null,
            }),
      },
    })
      .then(() => {
        onSuccess?.();
        onClose();
      })
      .catch((error) => {
        console.error('Error updating ticket:', error);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: glassPaperSx }, backdrop: { sx: glassBackdropSx } }}
    >
      <DialogTitle
        id="edit-ticket-dialog-title"
        sx={{
          color: NAVY,
          fontWeight: 700,
          fontFamily: FONT,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>Editar ticket</Box>
        <IconButton size="small" onClick={onClose} disabled={isLoading} sx={{ color: alpha(NAVY, 0.5) }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ fontFamily: FONT }}>
        <FormSection eyebrow="Problema" title="¿Qué pasó?" dense>
          <FormGrid columns={1} gap={2}>
            <FormField
              label="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!title.trim() ? 'El título es requerido' : undefined}
              disabled={isLoading}
              required
            />
            <FormField
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={!description.trim() ? 'La descripción es requerida' : undefined}
              disabled={isLoading}
              required
              multiline
              rows={4}
            />
          </FormGrid>
        </FormSection>

        {!isRollbarTicket && (
          <FormSection eyebrow="Clasificación" title="Prioridad y motivo" dense>
            <FormGrid columns={2} gap={2}>
              <FieldShell label="Motivo">
                <FormControl fullWidth disabled={isLoading}>
                  <Select
                    value={motive}
                    onChange={(event: SelectChangeEvent<string>) => setMotive(event.target.value as TicketMotive | '')}
                    displayEmpty
                    sx={multiSelectSx}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography sx={{ color: alpha(NAVY, 0.35), fontFamily: FONT, fontSize: '0.9375rem' }}>
                            Sin motivo
                          </Typography>
                        );
                      }
                      return TICKET_MOTIVE_LABELS[value as TicketMotive];
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: FONT }}>
                      <em>Sin motivo</em>
                    </MenuItem>
                    {SELECTABLE_TICKET_MOTIVES.map((m) => (
                      <MenuItem key={m} value={m} sx={{ fontFamily: FONT }}>
                        {TICKET_MOTIVE_LABELS[m]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldShell>

              <FieldShell label="Relevancia">
                <FormControl fullWidth disabled={isLoading}>
                  <Select
                    value={urgency}
                    onChange={(event: SelectChangeEvent<string>) =>
                      setUrgency(event.target.value as TicketUrgency | '')
                    }
                    displayEmpty
                    sx={multiSelectSx}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography sx={{ color: alpha(NAVY, 0.35), fontFamily: FONT, fontSize: '0.9375rem' }}>
                            Sin relevancia
                          </Typography>
                        );
                      }
                      const r = value as TicketUrgency;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: urgencyDotColor(r) }} />
                          <Typography sx={{ fontFamily: FONT, color: NAVY, fontSize: '0.9375rem' }}>
                            {TICKET_URGENCY_LABELS[r]}
                          </Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: FONT }}>
                      <em>Sin relevancia</em>
                    </MenuItem>
                    {TICKET_URGENCIES.map((r) => (
                      <MenuItem key={r} value={r} sx={{ fontFamily: FONT }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: urgencyDotColor(r) }} />
                          {TICKET_URGENCY_LABELS[r]}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldShell>

              <FieldShell label="Fecha tope estimada de solución" optional>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    value={deadlineDate}
                    onChange={(newValue) => setDeadlineDate(newValue)}
                    disabled={isLoading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: datePickerSx,
                      },
                      field: { clearable: true },
                    }}
                  />
                </LocalizationProvider>
              </FieldShell>
            </FormGrid>
          </FormSection>
        )}

        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: '0.75rem',
            color: alpha(NAVY, 0.55),
            letterSpacing: '0.03em',
            mt: 1,
          }}
        >
          ID: {ticket.id} · Creado: {new Date(ticket.createdAt).toLocaleString('es-CL')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Stack direction="row" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-end' }}>
          <BackButton onClick={onClose} disabled={isLoading}>
            Cancelar
          </BackButton>
          <PrimaryButton onClick={handleSubmit} disabled={isLoading || !title.trim() || !description.trim()}>
            {isLoading ? 'Guardando…' : 'Guardar cambios'}
          </PrimaryButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default EditTicketDialog;
